import { useMemo, useState } from "react";
import moment from "moment";

import { DATE_FORMATS } from "../../../../constants";
import {
  toUtcNumber,
  utcToDate,
  resolveQuickRange,
  rangeFromPlan,
  mostRecentPlan,
  plansInRange,
  resolvePlanStatus,
  getPlanTargetsSummary,
  hasRestTargets,
  hasAnnualPlanner,
  DEFAULT_QUICK_RANGE,
} from "../../../../utils/nutrition-progress";

/**
 * Owns the range/plan filter state for the new Progress screen and derives the
 * persistent-header facts from the engine. Kept separate from Nutrition.js so
 * the legacy view's own date state is untouched.
 *
 * Inputs are raw locals: nutritionPlans, the plan covering today, the athlete
 * (for the annual planner), and the bodyweight-goal flag (feature-flagged off
 * for now — wired when its own block lands).
 */
export const useNutritionProgressFilters = ({
  nutritionPlans = [],
  currentNutritionPlan,
  athlete,
  hasBodyweightGoal = false,
}) => {
  const todayUtc = toUtcNumber(new Date());

  const [selectedPlanId, setSelectedPlanId] = useState(
    currentNutritionPlan ? String(currentNutritionPlan._id) : null
  );
  // No current plan to anchor the range → the initial window is really the
  // DEFAULT_QUICK_RANGE fallback (see resolveQuickRange below), so the quick-
  // select bar should start with that same button marked active — otherwise
  // the displayed dates match "2 Weeks" exactly but no button shows pressed.
  const planRange = rangeFromPlan(currentNutritionPlan);
  const initialRange = planRange || resolveQuickRange(DEFAULT_QUICK_RANGE);
  const [dateFrom, setDateFrom] = useState(initialRange.dateFrom);
  const [dateTo, setDateTo] = useState(initialRange.dateTo);
  const [activeQuick, setActiveQuick] = useState(planRange ? null : DEFAULT_QUICK_RANGE);

  // The plan whose targets the range is measured against.
  // Explicit dropdown pick takes priority; otherwise the first plan whose span
  // overlaps the selected date range (so quick-select / manual dates don't bleed
  // in a plan that is entirely outside the window).
  const selectedPlan = useMemo(() => {
    if (selectedPlanId) {
      return nutritionPlans.find((p) => String(p._id) === selectedPlanId) || null;
    }
    const fromUtc = toUtcNumber(dateFrom);
    const toUtc = toUtcNumber(dateTo);
    return plansInRange(nutritionPlans, fromUtc, toUtc)[0] || null;
  }, [nutritionPlans, selectedPlanId, dateFrom, dateTo]);

  // Status bar describes the plan covering the selected range (null = NONE).
  // mostRecentPlan is still passed so resolvePlanStatus can detect the DUE state.
  const status = useMemo(() => {
    const recentPlan = mostRecentPlan(nutritionPlans);
    return {
      state: resolvePlanStatus({ activePlan: selectedPlan, recentPlan, todayUtc }),
      statusPlan: selectedPlan,
    };
  }, [nutritionPlans, selectedPlan, todayUtc]);

  // Returns the flags for the nutrition progress page
  const flags = useMemo(
    () => ({
      planStatus: status.state,
      hasPlanner: hasAnnualPlanner(athlete),
      hasRestTargets: hasRestTargets(status.statusPlan),
      hasBodyweightGoal,
    }),
    [status.state, status.statusPlan, athlete, hasBodyweightGoal]
  );

  // Returns the targets summary for the nutrition progress page
  const targetsSummary = useMemo(
    () => getPlanTargetsSummary(status.statusPlan),
    [status.statusPlan]
  );

  // --- actions ---------------------------------------------------------------

  // Pick a plan from the selector → snap the range to that plan's span.
  const selectPlan = (planId) => {
    setSelectedPlanId(planId);
    setActiveQuick(null);
    const range = rangeFromPlan(nutritionPlans.find((p) => String(p._id) === planId));
    if (range) {
      setDateFrom(range.dateFrom);
      setDateTo(range.dateTo);
    }
  };

  // Quick-select → trailing window ending today, detaches from any plan.
  // "lifetime" is a special case: snap to the earliest plan start date so the
  // full history is visible without an arbitrary 10-year hard-code.
  const applyQuickRange = (quickId) => {
    setActiveQuick(quickId);
    setSelectedPlanId(null);
    if (quickId === "lifetime" && nutritionPlans.length) {
      const earliestUtc = nutritionPlans.reduce(
        (min, p) => Math.min(min, p.utcStartDate),
        Infinity
      );
      setDateFrom(utcToDate(earliestUtc));
      setDateTo(new Date());
      return;
    }
    const range = resolveQuickRange(quickId);
    setDateFrom(range.dateFrom);
    setDateTo(range.dateTo);
  };

  // Manual From/To edit → custom range, clears the quick-select highlight.
  // Clamp to keep from ≤ to: moving From past To pulls To forward; moving To
  // before From pulls From back.
  const changeDate = (date, field) => {
    // DateInput's rome picker emits a display string ("MMM D, YYYY"), but the
    // rest of the range state is native Dates (initial range, quick-select).
    // Normalise here so dateFrom/dateTo stay Dates — keeps the < / >
    // comparisons below numeric and downstream consumers type-consistent.
    const next =
      date instanceof Date
        ? date
        : moment(date, DATE_FORMATS.DATE_PICKER_FORMAT).toDate();
    setActiveQuick(null);
    setSelectedPlanId(null);
    if (field === "dateTo") {
      setDateTo(next);
      if (next < dateFrom) setDateFrom(next);
    } else {
      setDateFrom(next);
      if (next > dateTo) setDateTo(next);
    }
  };

  return {
    // filter state
    selectedPlanId,
    selectedPlan,
    dateFrom,
    dateTo,
    activeQuick,
    // derived header facts
    planStatus: status.state,
    statusPlan: status.statusPlan,
    flags,
    targetsSummary,
    // actions
    selectPlan,
    applyQuickRange,
    changeDate,
  };
};
